import axios from 'axios';
import i18n from '../i18n';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 120000,
  withCredentials: true,
});

api.interceptors.request.use(config => {
  // Nessun header x-auth-token: il cookie lore_token viene inviato automaticamente dal browser
  return config;
});

api.interceptors.response.use(
  res => res.data,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('lore_auth_expiry');
      localStorage.removeItem('lore_auth_user');
      // Cancella il cookie lato server (fire-and-forget)
      api.post('/auth/logout').catch(() => {});
      window.location.href = '/';
      return;
    }
    const code = err.response?.data?.error || 'UNKNOWN';
    const detail = err.response?.data?.detail || err.message;
    const translated = i18n.t(code, { ns: 'errors', defaultValue: code });
    const e = new Error(translated);
    e.code = code;
    e.detail = detail;
    throw e;
  }
);

// ─── Instances ────────────────────────────────────────────────────────────────
export const instancesApi = {
  list: () => api.get('/instances'),
  get: id => api.get(`/instances/${id}`),
  create: data => api.post('/instances', data),
  update: (id, data) => api.put(`/instances/${id}`, data),
  delete: id => api.delete(`/instances/${id}`),
  stats: id => api.get(`/instances/${id}/stats`),
};

// ─── Documents ────────────────────────────────────────────────────────────────
export const documentsApi = {
  list: instanceId => api.get(`/documents/${instanceId}`),
  uploadFiles: (instanceId, files) => {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    return api.post(`/documents/${instanceId}/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  addUrl: (instanceId, url) => api.post(`/documents/${instanceId}/url`, { url }),
  delete: (instanceId, docId) => api.delete(`/documents/${instanceId}/${docId}`),
};

// ─── Q&A ──────────────────────────────────────────────────────────────────────
export const qaApi = {
  query: (instanceId, payload) => api.post(`/qa/${instanceId}`, payload),
  queryMulti: payload => api.post('/qa/multi', payload),
};

// ─── Prompts ──────────────────────────────────────────────────────────────────
export const promptsApi = {
  list: () => api.get('/prompts'),
  get: id => api.get(`/prompts/${id}`),
  create: data => api.post('/prompts', data),
  update: (id, data) => api.put(`/prompts/${id}`, data),
  delete: id => api.delete(`/prompts/${id}`),
  test: data => api.post('/prompts/test', data),
};

// ─── LLM ──────────────────────────────────────────────────────────────────────
export const llmApi = {
  providers: () => api.get('/llm/providers'),
  health: () => api.get('/llm/health'),
};

// ─── Agentic RAG ──────────────────────────────────────────────────────────────
// Dedicated client with extended timeout: agentic runs with local LLMs
// (Ollama) can legitimately take 3-5 minutes for full router→critic→synth.
const agentClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 600000, // 10 min
  withCredentials: true,
});
agentClient.interceptors.response.use(
  res => res.data,
  err => {
    if (err.response?.status === 401) { window.location.href = '/'; return; }
    const code = err.response?.data?.error || (err.code === 'ECONNABORTED' ? 'TIMEOUT' : 'UNKNOWN');
    const translated = i18n.t(code, { ns: 'errors', defaultValue: code });
    const e = new Error(translated);
    e.code = code;
    e.detail = err.response?.data?.detail || err.message;
    throw e;
  }
);

// SSE streaming: POST /agent/query/stream, parses event blocks and invokes
// handlers as the graph progresses.
//   handlers: { onOpen?, onStep?, onDone?, onError?, signal? }
// Returns a Promise that resolves when the stream ends.
async function agentQueryStream(payload, handlers = {}) {
  const url = (process.env.REACT_APP_API_URL || '/api') + '/agent/query/stream';
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(payload),
    signal: handlers.signal,
  });
  if (!res.ok) {
    let body = null;
    try { body = await res.json(); } catch {}
    const e = new Error(body?.error || `HTTP ${res.status}`);
    e.code = body?.error || 'HTTP_' + res.status;
    throw e;
  }
  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buf.indexOf('\n\n')) !== -1) {
      const block = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let event = 'message';
      const dataLines = [];
      for (const line of block.split('\n')) {
        if (line.startsWith('event:'))      event = line.slice(6).trim();
        else if (line.startsWith('data:'))  dataLines.push(line.slice(5).trim());
      }
      const dataStr = dataLines.join('\n');
      let data = null;
      if (dataStr) { try { data = JSON.parse(dataStr); } catch { data = dataStr; } }
      if (event === 'open'  && handlers.onOpen)  handlers.onOpen(data);
      if (event === 'step'  && handlers.onStep)  handlers.onStep(data);
      if (event === 'done'  && handlers.onDone)  handlers.onDone(data);
      if (event === 'error' && handlers.onError) handlers.onError(data);
    }
  }
}

export const agentApi = {
  config:       () => api.get('/agent/config'),
  updateConfig: (diff) => api.put('/agent/config', diff),
  dashboard:    (days = 30) => api.get(`/agent/dashboard?days=${days}`),
  activity:     (limit = 10) => api.get(`/agent/activity?limit=${limit}`),

  // Personas (verticali specializzati)
  personas:        () => api.get('/agent/personas'),
  personaGet:      id => api.get(`/agent/personas/${id}`),
  personaCreate:   data => api.post('/agent/personas', data),
  personaUpdate:   (id, data) => api.put(`/agent/personas/${id}`, data),
  personaDelete:   id => api.delete(`/agent/personas/${id}`),

  // Pulse SSE — calls onPulse(data) for every completed agent run
  pulseStream(handlers = {}) {
    const url = (process.env.REACT_APP_API_URL || '/api') + '/agent/pulse/stream';
    const es = new EventSource(url, { withCredentials: true });
    es.addEventListener('pulse', (e) => {
      try { handlers.onPulse?.(JSON.parse(e.data)); } catch {}
    });
    es.addEventListener('open',  (e) => handlers.onOpen?.());
    es.addEventListener('error', (e) => handlers.onError?.(e));
    return es; // caller calls es.close() to unsubscribe
  },
  tools:     () => api.get('/agent/tools'),
  query:     payload => agentClient.post('/agent/query', payload), // long-running
  queryStream: agentQueryStream,

  // Sessions (conversational memory)
  listSessions:    () => api.get('/agent/sessions'),
  createSession:   (data = {}) => api.post('/agent/sessions', data),
  getSession:      id => api.get(`/agent/sessions/${id}`),
  getMessages:     (id, limit = 100) => api.get(`/agent/sessions/${id}/messages?limit=${limit}`),
  deleteSession:   id => api.delete(`/agent/sessions/${id}`),

  // Facts (long-term memory)
  listFacts:    () => api.get('/agent/facts'),
  deleteFact:   id => api.delete(`/agent/facts/${id}`),

  // Observability
  listTraces:  (params = {}) => api.get('/agent/traces', { params }),
  getTrace:    id => api.get(`/agent/traces/${id}`),

  // Cost / cache
  cost:        (days = 30) => api.get(`/agent/cost?days=${days}`),
  cacheStats:  () => api.get('/agent/cache'),
  cacheClear:  () => api.delete('/agent/cache'),

  // A/B compare (long-running)
  compare:     payload => agentClient.post('/agent/compare', payload),

  // Eval suite
  evalRun:     payload => agentClient.post('/agent/eval', payload),
  evalList:    () => api.get('/agent/eval'),
  evalGet:     id => api.get(`/agent/eval/${id}`),

  // Export session as Markdown — returns a Blob URL for download
  exportSession: async (sessionId) => {
    const url = (process.env.REACT_APP_API_URL || '/api') + `/agent/sessions/${sessionId}/export`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('Export failed');
    return await res.blob();
  },
};

export default api;
